import ArticleLayout from '@/components/feature/ArticleLayout';

export default function ShippingCostArticle() {
  return (
    <ArticleLayout
      pageTitle="Koszty wysyłki Kakobuy do Polski – kompletny przewodnik 2026 | KakobuyFinds"
      title="Koszty wysyłki Kakobuy do Polski – wszystko, co musisz wiedzieć"
      description="Ile kosztuje wysyłka Kakobuy do Polski? Jak długo trwa? Porównanie metod dostawy, porady jak zaoszczędzić i praktyczne kalkulacje kosztów."
      category="Wysyłka"
      readTime="7 minut czytania"
      breadcrumbs={[{ label: 'Strona główna', href: '/' }, { label: 'Koszty wysyłki' }]}
      faqData={[
        { question: 'Jaki jest najtańszy sposób wysyłki Kakobuy do Polski?', answer: 'Wysyłka ekonomiczna (20-35 dni) to najtańsza opcja – kosztuje około 20-40 zł za małe paczki. Warto rozważyć łączenie kilku zamówień w jedną przesyłkę, aby obniżyć koszt na produkt.' },
        { question: 'Czy opłaca się płacić za szybszą wysyłkę?', answer: 'To zależy od pilności zamówienia. Ekspresowa wysyłka (DHL/FedEx) kosztuje 80-150 zł, ale trwa tylko 5-10 dni. Dla droższych zamówień szybsza wysyłka może być warta dodatkowych kosztów.' },
      ]}
      articleSchema={{ '@context': 'https://schema.org', '@type': 'Article', headline: 'Koszty wysyłki Kakobuy do Polski', description: 'Kompletny przewodnik po kosztach wysyłki.', author: { '@type': 'Organization', name: 'KakobuyFinds' }, datePublished: '2026-07-16', dateModified: '2026-07-16', inLanguage: 'pl', publisher: { '@type': 'Organization', name: 'KakobuyFinds' } }}
    >
      <section>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">1. Od czego zależą koszty wysyłki?</h2>
        <p className="text-sm md:text-base mb-4">
          Zrozumienie, co wpływa na koszty wysyłki, pomoże Ci lepiej planować zakupy z <strong>Kakobuy Spreadsheet</strong>. Oto główne czynniki:
        </p>
        <ul className="space-y-2 my-4 text-sm">
          <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i><span><strong className="text-foreground-800">Waga</strong> – najważniejszy czynnik. Każdy dodatkowy kilogram zwiększa koszt.</span></li>
          <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i><span><strong className="text-foreground-800">Wymiary</strong> – duża paczka kosztuje więcej, nawet jeśli jest lekka.</span></li>
          <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i><span><strong className="text-foreground-800">Metoda wysyłki</strong> – kurier kontra poczta, standard kontra ekspres.</span></li>
          <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i><span><strong className="text-foreground-800">Ubezpieczenie</strong> – opcjonalne, ale warte rozważenia dla droższych produktów.</span></li>
        </ul>
      </section>
      <section>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">2. Przykładowe koszty wysyłki</h2>
        <div className="overflow-x-auto my-6 rounded-xl border border-background-200/50">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary-50">
                <th className="text-left px-4 py-3 font-semibold">Produkt</th>
                <th className="text-center px-4 py-3 font-semibold">Waga</th>
                <th className="text-center px-4 py-3 font-semibold">Standard (15-25 dni)</th>
                <th className="text-center px-4 py-3 font-semibold text-primary-600">Ekspres (5-10 dni)</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['T-shirt', '~200g', '25-35 zł', '60-80 zł'],
                ['Bluza', '~500g', '35-50 zł', '70-100 zł'],
                ['Buty sportowe', '~800g', '45-65 zł', '80-120 zł'],
                ['Kurtka zimowa', '~1200g', '55-80 zł', '100-150 zł'],
                ['Zestaw ubrań', '~2000g', '80-120 zł', '130-180 zł'],
              ].map((row, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-background-50' : 'bg-secondary-50/50'}>
                  <td className="px-4 py-2.5 font-medium text-foreground-700">{row[0]}</td>
                  <td className="text-center px-4 py-2.5 text-foreground-600">{row[1]}</td>
                  <td className="text-center px-4 py-2.5 text-foreground-600">{row[2]}</td>
                  <td className="text-center px-4 py-2.5 text-primary-600">{row[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-foreground-500 italic">* Ceny orientacyjne, mogą się różnić w zależności od aktualnych stawek przewoźników.</p>
      </section>
      <section>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">3. Jak zaoszczędzić na wysyłce?</h2>
        <div className="space-y-3 my-6">
          {[
            'Łącz kilka produktów w jedno zamówienie – wysyłka jednej dużej paczki jest tańsza niż kilku małych.',
            'Korzystaj z ekonomicznych opcji wysyłki, jeśli nie zależy Ci na czasie – oszczędność nawet 40%.',
            'Sprawdzaj szacunkowe koszty w Kakobuy Spreadsheet przed zakupem – arkusz zawiera orientacyjne informacje.',
            'Unikaj zamawiania pojedynczych lekkich przedmiotów – koszt wysyłki może przekroczyć wartość produktu.',
          ].map((tip, idx) => (
            <div key={idx} className="flex items-start gap-2 p-3 bg-accent-50/60 rounded-xl text-sm">
              <span className="text-accent-500 mt-0.5 shrink-0 text-xs">💡</span>
              <span className="text-foreground-700">{tip}</span>
            </div>
          ))}
        </div>
      </section>
    </ArticleLayout>
  );
}